// Fetches and decodes an image before it's shown, so slides and ads appear complete instead
// of fading in to black while a large photo is still downloading. The loaded image element
// itself is kept and handed to whatever shows it (preloadedImage), so it's on screen at once
// with no second download – the browser's cache can't be relied on for that: OneDrive photos
// come through a redirect to a short-lived link, and some hosts don't allow caching.

const KEEP = 6;
const loaded = new Map<string, HTMLImageElement>();
const pending = new Map<string, Promise<boolean>>();

function keep(url: string, img: HTMLImageElement): void {
  loaded.delete(url);
  loaded.set(url, img);
  if (loaded.size > KEEP) loaded.delete(loaded.keys().next().value!);
}

/**
 * Resolves true once `url` is downloaded and decoded, false if it fails or takes longer than
 * `timeoutMs` (the download carries on in the background; a later call can still succeed).
 */
export function preloadImage(url: string, timeoutMs = 60_000): Promise<boolean> {
  if (loaded.has(url)) return Promise.resolve(true);
  let p = pending.get(url);
  if (!p) {
    p = new Promise<boolean>((resolve) => {
      const img = new Image();
      img.decoding = 'async';
      img.onload = () => {
        // decode() makes sure the first frame paints at once; older TV browsers lack it.
        const decoded = typeof img.decode === 'function' ? img.decode().catch(() => undefined) : Promise.resolve();
        void decoded.then(() => {
          keep(url, img);
          pending.delete(url);
          resolve(true);
        });
      };
      img.onerror = () => {
        pending.delete(url);
        resolve(false);
      };
      img.src = url;
    });
    pending.set(url, p);
  }
  return Promise.race([p, new Promise<boolean>((r) => window.setTimeout(() => r(false), timeoutMs))]);
}

/**
 * The already-loaded image for `url`, if it was preloaded – to put on screen as it is. One
 * that's already on screen elsewhere is copied (a copy of a loaded image needs no download
 * where the browser keeps it; otherwise it loads as normal).
 */
export function preloadedImage(url: string): HTMLImageElement | null {
  const img = loaded.get(url);
  if (!img) return null;
  return img.isConnected ? (img.cloneNode() as HTMLImageElement) : img;
}
